import Button from "react-bootstrap/Button";
import { useEffect, useMemo, useRef, useState } from "react";
import { FloatingLabel, Form } from "react-bootstrap";
import InsetAlert from "../components/insetalert";
import DotLoader from "../components/dotloader";
import OtpCodeInput from "../components/otp-code-input";
import styles from "../styles/auth-pages.module.css";
import { useForm } from "react-hook-form";
import {
    useDiscoverLoginMethodsMutation,
    useLoginMutation,
    usePasskeyLoginMutation,
    useRecoveryCodeLoginMutation,
    useResendPasswordLoginCodeMutation,
    useVerifyPasswordLoginCodeMutation
} from "../new/auth.query";

const PASSWORD_EMAIL_CODE_LENGTH = 6;
const RECOVERY_CODE_RAW_LENGTH = 12;
const OTP_ACCEPTED_REDIRECT_DELAY_MS = 1600;

const resolveSecondaryActionVisibility = (secondaryActionVisibility, stage) => {
    if (secondaryActionVisibility === "always") {
        return true;
    }

    if (Array.isArray(secondaryActionVisibility)) {
        return secondaryActionVisibility.includes(stage);
    }

    return secondaryActionVisibility === stage;
};

const resolvePreferredStage = (discovery, { allowPassword, allowPasskey, allowRecoveryCode }) => {
    if (discovery?.preferredMethod === "passkey" && discovery?.passkeyConfigured && allowPasskey) {
        return "passkey";
    }

    if (discovery?.passwordAllowed && allowPassword) {
        return "password";
    }

    if (discovery?.passkeyConfigured && allowPasskey) {
        return "passkey";
    }

    if (discovery?.recoveryCodeAllowed && allowRecoveryCode) {
        return "recoveryCode";
    }

    return "password";
};

const normalizeEmailCode = (value) => (value ?? "")
    .replace(/\D/g, "")
    .slice(0, PASSWORD_EMAIL_CODE_LENGTH);

const normalizeRecoveryCodeValue = (value) => (value ?? "")
    .replace(/[^a-fA-F0-9]/g, "")
    .toUpperCase()
    .slice(0, RECOVERY_CODE_RAW_LENGTH);

const formatRecoveryCodeValue = (value) => normalizeRecoveryCodeValue(value)
    .replace(/(.{4})(?=.)/g, "$1-");

const formatOtpCountdown = (remainingMs) => {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

function LoginForm({
    defaultUsername = "",
    onSuccess = null,
    secondaryActionLabel = null,
    onSecondaryAction = null,
    secondaryActionVariant = "link",
    secondaryActionClassName = "ps-0",
    secondaryActionVisibility = "always",
    submitLabel = "Continue",
    usernameReadOnly = false,
    focusPassword = false,
    allowPassword = true,
    allowPasskey = true,
    allowRecoveryCode = true
}) {
    const [stage, setStage] = useState("username");
    const [discovery, setDiscovery] = useState(null);
    const [passwordChallenge, setPasswordChallenge] = useState(null);
    const [resolvedUsername, setResolvedUsername] = useState(defaultUsername ?? "");
    const [autoDiscoveryAttempted, setAutoDiscoveryAttempted] = useState(false);
    const [emailOtpNotice, setEmailOtpNotice] = useState(null);
    const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());
    const [otpAccepted, setOtpAccepted] = useState(false);
    const [passkeyAccepted, setPasskeyAccepted] = useState(false);
    const passwordAttemptRef = useRef("");
    const autoRefreshAttemptRef = useRef(null);
    const refreshPasswordChallengeRef = useRef(null);
    const otpInputRef = useRef(null);
    const otpAcceptedTimerRef = useRef(null);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        watch,
        formState: { errors, isSubmitting }
    } = useForm({
        defaultValues: {
            Username: defaultUsername ?? "",
            Password: "",
            RecoveryCode: "",
            EmailCode: ""
        }
    });

    const emailCodeValue = normalizeEmailCode(watch("EmailCode"));
    const recoveryCodeValue = formatRecoveryCodeValue(watch("RecoveryCode"));

    const discoverMutation = useDiscoverLoginMethodsMutation((data) => {
        passwordAttemptRef.current = "";
        autoRefreshAttemptRef.current = null;
        if (otpAcceptedTimerRef.current) {
            window.clearTimeout(otpAcceptedTimerRef.current);
            otpAcceptedTimerRef.current = null;
        }
        setDiscovery(data);
        setResolvedUsername(data?.username ?? defaultUsername ?? "");
        setPasswordChallenge(null);
        setEmailOtpNotice(null);
        setOtpAccepted(false);
        setPasskeyAccepted(false);
        setStage(resolvePreferredStage(data, { allowPassword, allowPasskey, allowRecoveryCode }));
        setValue("Password", "");
        setValue("RecoveryCode", "");
        setValue("EmailCode", "");
    });

    const loginMutation = useLoginMutation();

    const verifyPasswordCodeMutation = useVerifyPasswordLoginCodeMutation((session, finalizeSession) => {
        passwordAttemptRef.current = "";
        autoRefreshAttemptRef.current = null;
        setEmailOtpNotice(null);
        setOtpAccepted(true);
        setValue("EmailCode", normalizeEmailCode(watch("EmailCode")));

        if (otpAcceptedTimerRef.current) {
            window.clearTimeout(otpAcceptedTimerRef.current);
        }

        otpAcceptedTimerRef.current = window.setTimeout(() => {
            otpAcceptedTimerRef.current = null;
            finalizeSession?.();
            if (onSuccess) {
                onSuccess(session);
            }
        }, OTP_ACCEPTED_REDIRECT_DELAY_MS);
    }, null, {
        applySessionOnSuccess: false
    });

    const resendPasswordCodeMutation = useResendPasswordLoginCodeMutation();

    const passkeyLoginMutation = usePasskeyLoginMutation((session, finalizeSession) => {
        passwordAttemptRef.current = "";
        autoRefreshAttemptRef.current = null;
        setEmailOtpNotice(null);
        setOtpAccepted(false);
        setPasskeyAccepted(true);

        if (otpAcceptedTimerRef.current) {
            window.clearTimeout(otpAcceptedTimerRef.current);
        }

        otpAcceptedTimerRef.current = window.setTimeout(() => {
            otpAcceptedTimerRef.current = null;
            finalizeSession?.();
            if (onSuccess) {
                onSuccess(session);
            }
        }, OTP_ACCEPTED_REDIRECT_DELAY_MS);
    }, null, {
        applySessionOnSuccess: false
    });

    const recoveryCodeLoginMutation = useRecoveryCodeLoginMutation((data) => {
        passwordAttemptRef.current = "";
        autoRefreshAttemptRef.current = null;
        setEmailOtpNotice(null);

        if (onSuccess) {
            onSuccess(data);
        }
    });

    useEffect(() => {
        reset({
            Username: defaultUsername ?? "",
            Password: "",
            RecoveryCode: "",
            EmailCode: ""
        });
        passwordAttemptRef.current = "";
        autoRefreshAttemptRef.current = null;
        if (otpAcceptedTimerRef.current) {
            window.clearTimeout(otpAcceptedTimerRef.current);
            otpAcceptedTimerRef.current = null;
        }
        setResolvedUsername(defaultUsername ?? "");
        setDiscovery(null);
        setPasswordChallenge(null);
        setEmailOtpNotice(null);
        setOtpAccepted(false);
        setPasskeyAccepted(false);
        setStage("username");
        setAutoDiscoveryAttempted(false);
    }, [defaultUsername, reset]);

    useEffect(() => {
        return () => {
            if (otpAcceptedTimerRef.current) {
                window.clearTimeout(otpAcceptedTimerRef.current);
                otpAcceptedTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (usernameReadOnly && defaultUsername && !autoDiscoveryAttempted) {
            setAutoDiscoveryAttempted(true);
            setResolvedUsername(defaultUsername);
            setStage("username");

            discoverMutation.mutate({
                Username: defaultUsername
            });
        }
    }, [usernameReadOnly, defaultUsername, autoDiscoveryAttempted, discoverMutation]);

    useEffect(() => {
        if (stage !== "emailOtp" || !passwordChallenge?.expiresAt) {
            return;
        }

        setCurrentTimeMs(Date.now());

        const timerId = window.setInterval(() => {
            setCurrentTimeMs(Date.now());
        }, 250);

        return () => window.clearInterval(timerId);
    }, [stage, passwordChallenge?.challengeId, passwordChallenge?.expiresAt]);

    const resetMutationErrors = () => {
        discoverMutation.reset();
        loginMutation.reset();
        verifyPasswordCodeMutation.reset();
        resendPasswordCodeMutation.reset();
        passkeyLoginMutation.reset();
        recoveryCodeLoginMutation.reset();
    };

    const resetWorkflow = (nextStage = "username") => {
        passwordAttemptRef.current = "";
        autoRefreshAttemptRef.current = null;
        if (otpAcceptedTimerRef.current) {
            window.clearTimeout(otpAcceptedTimerRef.current);
            otpAcceptedTimerRef.current = null;
        }
        clearErrors();
        resetMutationErrors();
        setDiscovery(null);
        setPasswordChallenge(null);
        setEmailOtpNotice(null);
        setOtpAccepted(false);
        setPasskeyAccepted(false);
        setStage(nextStage);
        setValue("Password", "");
        setValue("RecoveryCode", "");
        setValue("EmailCode", "");
    };

    const applyPasswordChallenge = (challenge, notice = null) => {
        if (!challenge) {
            return null;
        }

        autoRefreshAttemptRef.current = null;
        if (otpAcceptedTimerRef.current) {
            window.clearTimeout(otpAcceptedTimerRef.current);
            otpAcceptedTimerRef.current = null;
        }
        setPasswordChallenge(challenge);
        setStage("emailOtp");
        setEmailOtpNotice(notice);
        setOtpAccepted(false);
        setPasskeyAccepted(false);
        setValue("Password", "");
        setValue("EmailCode", "");
        clearErrors("EmailCode");
        setCurrentTimeMs(Date.now());
        return challenge;
    };

    const handleDiscover = async (username) => {
        resetMutationErrors();

        try {
            await discoverMutation.mutateAsync({
                Username: username
            });
        } catch {
            // Expected discovery failures are displayed by the mutation error state.
        }
    };

    const beginFreshPasswordChallenge = async (password, notice = null) => {
        const latestChallenge = await loginMutation.mutateAsync({
            Username: resolvedUsername,
            Password: password
        });

        return applyPasswordChallenge(latestChallenge, notice);
    };

    const refreshPasswordChallenge = async ({ automatic = false, restartIfExpired = false } = {}) => {
        resetMutationErrors();
        clearErrors("EmailCode");

        try {
            const latestChallenge = await resendPasswordCodeMutation.mutateAsync({
                Username: resolvedUsername,
                ChallengeId: passwordChallenge?.challengeId
            });

            return applyPasswordChallenge(
                latestChallenge,
                automatic ? "That code expired, so we emailed a new one automatically." : "We emailed a new code. Only the latest code will work."
            );
        } catch {
            if (!restartIfExpired || !passwordAttemptRef.current || !resolvedUsername) {
                return null;
            }

            try {
                return await beginFreshPasswordChallenge(
                    passwordAttemptRef.current,
                    automatic ? "That code expired, so we emailed a new one automatically." : "That code had expired, so we emailed a new one."
                );
            } catch {
                return null;
            }
        }
    };

    refreshPasswordChallengeRef.current = refreshPasswordChallenge;

    const onSubmit = async (values) => {
        resetMutationErrors();

        try {
            if (stage === "username") {
                await handleDiscover(values.Username);
                return;
            }

            if (stage === "passkey") {
                setPasskeyAccepted(false);
                await passkeyLoginMutation.mutateAsync({
                    username: resolvedUsername
                });
                return;
            }

            if (stage === "password") {
                passwordAttemptRef.current = values.Password;
                setEmailOtpNotice(null);
                await beginFreshPasswordChallenge(values.Password);
                return;
            }

            if (stage === "emailOtp") {
                setOtpAccepted(false);
                await verifyPasswordCodeMutation.mutateAsync({
                    Username: resolvedUsername,
                    ChallengeId: passwordChallenge?.challengeId,
                    Code: normalizeEmailCode(values.EmailCode)
                });
                return;
            }

            if (stage === "recoveryCode") {
                await recoveryCodeLoginMutation.mutateAsync({
                    username: resolvedUsername,
                    recoveryCode: values.RecoveryCode
                });
            }
        } catch {
            // Expected auth failures are surfaced through the mutation state and UI alert.
        }
    };

    const activeError = discoverMutation.error
        || loginMutation.error
        || verifyPasswordCodeMutation.error
        || resendPasswordCodeMutation.error
        || passkeyLoginMutation.error
        || recoveryCodeLoginMutation.error;
    const submitting = isSubmitting
        || discoverMutation.isPending
        || loginMutation.isPending
        || verifyPasswordCodeMutation.isPending
        || resendPasswordCodeMutation.isPending
        || passkeyLoginMutation.isPending
        || recoveryCodeLoginMutation.isPending;
    const interactionLocked = submitting || otpAccepted || passkeyAccepted;
    const showSecondaryAction = !!onSecondaryAction
        && !!secondaryActionLabel
        && resolveSecondaryActionVisibility(secondaryActionVisibility, stage);
    const showRecoveryAssistAction = stage === "username"
        && !!onSecondaryAction
        && !!secondaryActionLabel
        && typeof activeError?.message === "string"
        && activeError.message.includes("reset your password");
    const emailHint = passwordChallenge?.emailAddressHint || discovery?.emailAddressHint;
    const otpRemainingMs = useMemo(() => {
        if (stage !== "emailOtp" || !passwordChallenge?.expiresAt) {
            return 0;
        }

        return Math.max(new Date(passwordChallenge.expiresAt).getTime() - currentTimeMs, 0);
    }, [stage, passwordChallenge?.expiresAt, currentTimeMs]);
    const otpCountdownLabel = useMemo(() => formatOtpCountdown(otpRemainingMs), [otpRemainingMs]);
    const otpHasExpired = stage === "emailOtp" && !!passwordChallenge?.expiresAt && otpRemainingMs <= 0;
    const primaryLabel = useMemo(() => {
        switch (stage) {
            case "passkey":
                return "Continue with Passkey";
            case "password":
                return submitLabel;
            case "emailOtp":
                return "Verify Code";
            case "recoveryCode":
                return "Use Recovery Code";
            default:
                return "Continue";
        }
    }, [stage, submitLabel]);

    useEffect(() => {
        if (stage !== "emailOtp" || !passwordChallenge?.challengeId || interactionLocked) {
            return;
        }

        const focusTimer = window.setTimeout(() => {
            otpInputRef.current?.focusFirstEmpty?.();
        }, 0);

        return () => window.clearTimeout(focusTimer);
    }, [stage, passwordChallenge?.challengeId, interactionLocked]);

    useEffect(() => {
        if (stage !== "emailOtp" || !passwordChallenge?.challengeId || !otpHasExpired) {
            return;
        }

        if (emailCodeValue.length >= PASSWORD_EMAIL_CODE_LENGTH || interactionLocked) {
            return;
        }

        if (autoRefreshAttemptRef.current === passwordChallenge.challengeId) {
            return;
        }

        autoRefreshAttemptRef.current = passwordChallenge.challengeId;

        void refreshPasswordChallengeRef.current?.({
            automatic: true,
            restartIfExpired: true
        });
    }, [stage, passwordChallenge?.challengeId, otpHasExpired, emailCodeValue.length, interactionLocked]);

    const handleManualResendCode = async () => {
        await refreshPasswordChallenge({
            automatic: false,
            restartIfExpired: otpHasExpired
        });
    };

    const handleEmailCodeChange = (nextValue) => {
        setEmailOtpNotice(null);
        setValue("EmailCode", nextValue, {
            shouldDirty: true,
            shouldValidate: nextValue.length === PASSWORD_EMAIL_CODE_LENGTH
        });

        if (nextValue.length < PASSWORD_EMAIL_CODE_LENGTH) {
            clearErrors("EmailCode");
        }
    };

    const handleEmailCodeComplete = (nextValue) => {
        handleEmailCodeChange(nextValue);

        if (stage !== "emailOtp" || interactionLocked || otpHasExpired) {
            return;
        }

        window.setTimeout(() => {
            void handleSubmit(onSubmit)();
        }, 0);
    };

    const handleRecoveryCodeChange = (event) => {
        const nextValue = formatRecoveryCodeValue(event.target.value);

        setValue("RecoveryCode", nextValue, {
            shouldDirty: true,
            shouldValidate: normalizeRecoveryCodeValue(nextValue).length === RECOVERY_CODE_RAW_LENGTH
        });

        if (normalizeRecoveryCodeValue(nextValue).length < RECOVERY_CODE_RAW_LENGTH) {
            clearErrors("RecoveryCode");
        }
    };

    const recoveryCodeRegistration = register("RecoveryCode", {
        validate: (value) => (
            stage !== "recoveryCode"
            || normalizeRecoveryCodeValue(value).length === RECOVERY_CODE_RAW_LENGTH
            || "Enter the recovery code in the format XXXX-XXXX-XXXX."
        )
    });

    return (
        <>
            {activeError ? (
                <InsetAlert error={{ error: activeError.message }} heading="Login Failed" style={{ width: "100%" }} />
            ) : null}

            <Form onSubmit={handleSubmit(onSubmit)}>
                <FloatingLabel controlId="login-username" label="Username" className="mb-3">
                    <Form.Control
                        type="text"
                        placeholder="Username"
                        autoComplete="username"
                        isInvalid={!!errors.Username}
                        // Disabled inputs can be read as undefined during submit/blur validation.
                        readOnly={interactionLocked || stage !== "username" || usernameReadOnly}
                        autoFocus={stage === "username"}
                        {...register("Username", {
                            validate: (value) => value ? true : "Required"
                        })}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.Username?.message}
                    </Form.Control.Feedback>
                </FloatingLabel>

                {stage === "password" ? (
                    <>
                        <p className={`${styles.subtitle} mb-3`}>
                            Enter your password. We will send a 6-digit login code to {emailHint || "your email address"} that stays valid for 5 minutes before signing you in.
                        </p>
                        <FloatingLabel controlId="login-password" label="Password" className="mb-3">
                            <Form.Control
                                type="password"
                                placeholder="Password"
                                autoComplete="current-password"
                                isInvalid={!!errors.Password}
                                readOnly={interactionLocked}
                                autoFocus={focusPassword || stage === "password"}
                                {...register("Password", {
                                    validate: (value) => stage !== "password" || value ? true : "Required"
                                })}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.Password?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                        {!usernameReadOnly ? (
                            <Button type="button" variant="link" className="ps-0 mb-3" disabled={interactionLocked} onClick={() => resetWorkflow("username")}>
                                Use Different Username
                            </Button>
                        ) : null}
                    </>
                ) : null}

                {stage === "passkey" ? (
                    <div className="mb-3">
                        {passkeyAccepted ? (
                            <div className={styles.otpSuccessWrap} role="status" aria-live="polite">
                                <span className={styles.otpSuccessIcon} aria-hidden="true" />
                                <div>
                                    <p className={styles.otpSuccessTitle}>Passkey accepted</p>
                                    <p className={styles.otpSuccessCopy}>Signing you into the app now.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className={`${styles.subtitle} mb-2`}>
                                    This account requires passkey sign-in. Approve the request with your device biometrics, security key, or PIN.
                                </p>
                                <div className="d-flex flex-wrap gap-2">
                                    {allowPassword && discovery?.passwordAllowed ? (
                                        <Button type="button" variant="outline-secondary" disabled={interactionLocked} onClick={() => setStage("password")}>
                                            Use Password Instead
                                        </Button>
                                    ) : null}
                                    {allowRecoveryCode && discovery?.recoveryCodeAllowed ? (
                                        <Button type="button" variant="outline-secondary" disabled={interactionLocked} onClick={() => setStage("recoveryCode")}>
                                            Use Recovery Code
                                        </Button>
                                    ) : null}
                                    {!usernameReadOnly ? (
                                        <Button type="button" variant="link" className="ps-0" disabled={interactionLocked} onClick={() => resetWorkflow("username")}>
                                            Use Different Username
                                        </Button>
                                    ) : null}
                                </div>
                            </>
                        )}
                    </div>
                ) : null}

                {stage === "emailOtp" ? (
                    <>
                        <p className={`${styles.subtitle} mb-3`}>
                            Enter the 6-digit code we sent to {emailHint || "your email address"}. It stays valid for 5 minutes, fills one box at a time, and submits automatically when complete.
                        </p>
                        <input
                            type="hidden"
                            {...register("EmailCode", {
                                validate: (value) => (
                                    stage !== "emailOtp"
                                    || normalizeEmailCode(value).length === PASSWORD_EMAIL_CODE_LENGTH
                                    || "Enter the 6-digit code."
                                )
                            })}
                        />
                        <OtpCodeInput
                            ref={otpInputRef}
                            key={passwordChallenge?.challengeId ?? "email-otp"}
                            value={emailCodeValue}
                            length={PASSWORD_EMAIL_CODE_LENGTH}
                            disabled={interactionLocked}
                            isInvalid={!!errors.EmailCode}
                            success={otpAccepted}
                            autoFocus={true}
                            focusKey={passwordChallenge?.challengeId ?? "email-otp"}
                            onChange={handleEmailCodeChange}
                            onComplete={handleEmailCodeComplete}
                        />
                        {errors.EmailCode ? (
                            <Form.Control.Feedback type="invalid" className="d-block mb-3">
                                {errors.EmailCode.message}
                            </Form.Control.Feedback>
                        ) : null}
                        {otpAccepted ? (
                            <div className={styles.otpSuccessWrap} role="status" aria-live="polite">
                                <span className={styles.otpSuccessIcon} aria-hidden="true" />
                                <div>
                                    <p className={styles.otpSuccessTitle}>Code accepted</p>
                                    <p className={styles.otpSuccessCopy}>Signing you into the app now.</p>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.otpMetaRow}>
                                <p className={`${styles.otpTimer} ${otpHasExpired ? styles.otpTimerExpired : ""}`}>
                                    {otpHasExpired ? "Refreshing code..." : `Code expires in ${otpCountdownLabel}`}
                                </p>
                                <p className={styles.otpNote}>
                                    {emailOtpNotice || "Only the latest emailed code will work. If this one expires, we will send a new code automatically."}
                                </p>
                            </div>
                        )}
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <Button type="button" variant="outline-secondary" disabled={interactionLocked} onClick={handleManualResendCode}>
                                Send Another Code
                            </Button>
                            {allowPasskey && discovery?.passkeyConfigured ? (
                                <Button
                                    type="button"
                                    variant="link"
                                    className="ps-0"
                                    disabled={interactionLocked}
                                    onClick={() => {
                                        resetMutationErrors();
                                        setEmailOtpNotice(null);
                                        setOtpAccepted(false);
                                        setPasskeyAccepted(false);
                                        setValue("EmailCode", "");
                                        setStage("passkey");
                                    }}
                                >
                                    Use Required Passkey Instead
                                </Button>
                            ) : null}
                            {!usernameReadOnly ? (
                                <Button
                                    type="button"
                                    variant="link"
                                    className="ps-0"
                                    disabled={interactionLocked}
                                    onClick={() => resetWorkflow("username")}
                                >
                                    Use Different Username
                                </Button>
                            ) : null}
                        </div>
                    </>
                ) : null}

                {stage === "recoveryCode" ? (
                    <>
                        <p className={`${styles.subtitle} mb-3`}>
                            Enter one of your saved recovery codes. Each recovery code can only be used once.
                        </p>
                        <FloatingLabel controlId="login-recovery-code" label="Recovery Code" className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="XXXX-XXXX-XXXX"
                                autoComplete="one-time-code"
                                isInvalid={!!errors.RecoveryCode}
                                readOnly={interactionLocked}
                                spellCheck={false}
                                className={styles.recoveryCodeInput}
                                inputMode="text"
                                maxLength={RECOVERY_CODE_RAW_LENGTH + 2}
                                aria-describedby="recovery-code-format"
                                value={recoveryCodeValue}
                                {...recoveryCodeRegistration}
                                onChange={handleRecoveryCodeChange}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.RecoveryCode?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                        <p id="recovery-code-format" className={styles.recoveryCodeHint}>
                            Format: XXXX-XXXX-XXXX
                        </p>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {allowPasskey && discovery?.passkeyConfigured ? (
                                <Button type="button" variant="outline-secondary" disabled={interactionLocked} onClick={() => setStage("passkey")}>
                                    Use Required Passkey Instead
                                </Button>
                            ) : null}
                            {allowPassword && discovery?.passwordAllowed ? (
                                <Button type="button" variant="outline-secondary" disabled={interactionLocked} onClick={() => setStage("password")}>
                                    Use Password Instead
                                </Button>
                            ) : null}
                            {!usernameReadOnly ? (
                                <Button type="button" variant="link" className="ps-0" disabled={interactionLocked} onClick={() => resetWorkflow("username")}>
                                    Use Different Username
                                </Button>
                            ) : null}
                        </div>
                    </>
                ) : null}
                {showRecoveryAssistAction ? (
                    <Button
                        type="button"
                        variant="link"
                        className="ps-0 mb-3"
                        disabled={interactionLocked}
                        onClick={onSecondaryAction}
                    >
                        {secondaryActionLabel}
                    </Button>
                ) : null}

                <div className={styles.actionRow}>
                    {showSecondaryAction ? (
                        <Button
                            type="button"
                            variant={secondaryActionVariant}
                            className={secondaryActionClassName}
                            disabled={interactionLocked}
                            onClick={onSecondaryAction}
                        >
                            {secondaryActionLabel}
                        </Button>
                    ) : (
                        <div />
                    )}
                    <Button className={styles.primaryButton} variant="primary" type="submit" disabled={interactionLocked}>
                        {submitting ? <DotLoader ButtonStyle={true} /> : <>{primaryLabel}</>}
                    </Button>
                </div>
            </Form>
        </>
    );
}

export default LoginForm;
